/**
 * VoiceAssistantUI - Visual interface for voice assistant
 * 
 * Features:
 * - Microphone button with visual states
 * - Real-time transcript display
 * - Assistant response display
 * - Error messages
 * - Browser compatibility warnings
 * - Theme-aware styling
 */

import React, { useState, useEffect, useRef } from 'react';
import { MdMic, MdMicOff, MdGraphicEq, MdVolumeUp, MdAutorenew, MdDragIndicator, MdClose } from 'react-icons/md';
import themeAwareStyles from '../utils/ThemeAwareStyles';

const VoiceAssistantUI = ({
  isListening,
  isProcessing,
  isSpeaking,
  isActive,
  message,
  onStart,
  onStop,
  error,
  isSupported,
  continuousMode = false,
  onToggleContinuousMode,
  currentSuggestions = [],
  workflowState = null,
  voiceLang = 'english',
  onChangeVoiceLang = null
}) => {
  // Detect and track theme
  const [theme, setTheme] = useState(themeAwareStyles.detectTheme());

  // Draggable position (persisted). null = default bottom-right corner.
  const containerRef = useRef(null);
  const [pos, setPos] = useState(() => {
    try { const s = localStorage.getItem('vaPos'); return s ? JSON.parse(s) : null; } catch { return null; }
  });

  // Hidden state (persisted) — lets the user dismiss the mic so it isn't always on screen.
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem('vaHidden') === '1'; } catch (e) { return false; }
  });
  const hideAssistant = () => {
    setHidden(true);
    try { localStorage.setItem('vaHidden', '1'); } catch (e) { /* noop */ }
    if (onStop) onStop(); // stop any active listening
  };
  const showAssistant = () => {
    setHidden(false);
    try { localStorage.setItem('vaHidden', '0'); } catch (e) { /* noop */ }
  };

  const startDrag = (clientX, clientY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const startLeft = rect.left, startTop = rect.top;
    const W = 70, H = 70; // keep the handle on-screen
    const move = (cx, cy) => {
      const left = Math.min(window.innerWidth - W, Math.max(0, startLeft + cx - clientX));
      const top = Math.min(window.innerHeight - H, Math.max(0, startTop + cy - clientY));
      setPos({ left, top });
    };
    const onMouseMove = (e) => { e.preventDefault(); move(e.clientX, e.clientY); };
    const onTouchMove = (e) => { if (e.touches[0]) move(e.touches[0].clientX, e.touches[0].clientY); };
    const stop = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', stop);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', stop);
      setPos((p) => { try { if (p) localStorage.setItem('vaPos', JSON.stringify(p)); } catch (e) { /* noop */ } return p; });
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stop);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', stop);
  };

  // Update theme when it changes
  useEffect(() => {
    const updateTheme = () => {
      setTheme(themeAwareStyles.detectTheme());
    };

    // Listen for theme changes
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    darkModeQuery.addEventListener('change', updateTheme);

    // Also listen for class changes on body
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      darkModeQuery.removeEventListener('change', updateTheme);
      observer.disconnect();
    };
  }, []);

  // Determine button state
  const getButtonState = () => {
    if (isListening) return 'listening';
    if (isProcessing) return 'processing';
    if (isSpeaking) return 'speaking';
    if (isActive) return 'active'; // Active but waiting
    return 'idle';
  };

  const buttonState = getButtonState();

  // Button styles based on state
  const getButtonStyles = () => {
    const baseStyles = {
      minWidth: '88px',
      height: '40px',
      padding: '0 1rem',
      borderRadius: '6px',
      border: '1px solid transparent',
      cursor: isSupported ? 'pointer' : 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      transition: 'background-color 0.15s ease',
      boxShadow: '0 2px 8px rgba(17, 24, 39, 0.15)',
      position: 'relative',
      opacity: isSupported ? 1 : 0.5
    };

    const stateStyles = {
      idle: {
        background: '#1f3a8a',
        color: '#fff'
      },
      active: {
        background: '#1f3a8a',
        color: '#fff',
        opacity: 0.8
      },
      listening: {
        background: '#b42318',
        color: '#fff'
      },
      processing: {
        background: '#1d4ed8',
        color: '#fff'
      },
      speaking: {
        background: '#15803d',
        color: '#fff'
      }
    };

    return { ...baseStyles, ...stateStyles[buttonState] };
  };

  // Button content based on state: a state icon paired with a short label.
  const getButtonIcon = () => {
    const size = 18;
    if (!isSupported) return <><MdMicOff size={size} aria-hidden="true" /><span>Voice off</span></>;
    switch (buttonState) {
      case 'listening':
        return <><MdGraphicEq size={size} style={{ animation: 'vaBars 1.1s ease-in-out infinite' }} aria-hidden="true" /><span>Stop</span></>;
      case 'processing':
        return <><MdAutorenew size={size} style={{ animation: 'vaSpin 0.9s linear infinite' }} aria-hidden="true" /><span>Working</span></>;
      case 'speaking':
        return <><MdVolumeUp size={size} aria-hidden="true" /><span>Stop</span></>;
      default:
        return <><MdMic size={size} aria-hidden="true" /><span>Voice</span></>;
    }
  };

  // Button label
  const getButtonLabel = () => {
    switch (buttonState) {
      case 'listening':
        return 'Listening...';
      case 'processing':
        return 'Processing...';
      case 'speaking':
        return 'Speaking...';
      case 'active':
        return 'Click to Stop';
      default:
        return 'Start Voice Assistant';
    }
  };

  const handleClick = () => {
    if (!isSupported) return;
    
    if (isActive || isListening || isProcessing || isSpeaking) {
      // Stop the assistant if it's active in any way
      onStop();
    } else {
      // Start the assistant
      onStart();
    }
  };

  // When hidden, show only a tiny restore button so the assistant is recoverable.
  if (hidden) {
    return (
      <button
        onClick={showAssistant}
        title="Show voice assistant"
        style={{
          position: 'fixed',
          ...(pos ? { left: pos.left, top: pos.top, right: 'auto', bottom: 'auto' } : { bottom: '1.25rem', right: '1.25rem' }),
          zIndex: 1000,
          height: 32, padding: '0 0.75rem', borderRadius: '6px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
          fontSize: '0.75rem', fontWeight: 600,
          border: theme === 'dark' ? '1px solid #374151' : '1px solid #cfd6df',
          background: theme === 'dark' ? '#1f2937' : '#ffffff',
          color: theme === 'dark' ? '#e5e7eb' : '#4b5563',
          boxShadow: '0 2px 8px rgba(17, 24, 39, 0.12)', opacity: 0.95,
        }}
      >
        <MdMic size={14} aria-hidden="true" />
        Voice
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        ...(pos
          ? { left: pos.left, top: pos.top, right: 'auto', bottom: 'auto' }
          : { bottom: '2rem', right: '2rem' }),
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '1rem'
      }}
    >
      {/* Message bubble */}
      {(message || error) && (
        <div style={{
          maxWidth: 'min(320px, calc(100vw - 2rem))',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          background: error
            ? themeAwareStyles.getBackgroundColor(theme, 'error')
            : themeAwareStyles.getBackgroundColor(theme, 'assistant'),
          border: error
            ? `1px solid ${themeAwareStyles.getTextColor(theme, 'error')}`
            : theme === 'dark' ? '1px solid #374151' : '1px solid #e3e7ed',
          boxShadow: theme === 'dark'
            ? '0 4px 12px rgba(0, 0, 0, 0.4)'
            : '0 4px 12px rgba(17, 24, 39, 0.08)',
          fontSize: '0.875rem',
          color: error 
            ? themeAwareStyles.getTextColor(theme, 'error')
            : themeAwareStyles.getTextColor(theme, 'assistant'),
          lineHeight: '1.5'
        }}>
          {error ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Error</div>
                <div>{error.message}</div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <div>{message}</div>
            </div>
          )}
        </div>
      )}

      {/* Browser compatibility warning */}
      {!isSupported && (
        <div style={{
          maxWidth: 'min(320px, calc(100vw - 2rem))',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          background: '#fdf3e3',
          border: '1px solid #f0d3a6',
          boxShadow: '0 4px 12px rgba(17, 24, 39, 0.08)',
          fontSize: '0.875rem',
          color: '#b45309',
          lineHeight: '1.5'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                Voice Assistant Not Supported
              </div>
              <div>
                Your browser doesn't support voice recognition. Please use Chrome, Edge, or Safari.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Continuous Mode Toggle - Only show if onToggleContinuousMode is provided */}
      {isSupported && onToggleContinuousMode && (
        <div style={{
          background: theme === 'dark' ? '#1f2937' : '#ffffff',
          border: theme === 'dark' ? '1px solid #374151' : '1px solid #e3e7ed',
          borderRadius: '8px',
          padding: '0.5rem 0.75rem',
          boxShadow: theme === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.4)'
            : '0 2px 8px rgba(17, 24, 39, 0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          cursor: 'pointer',
          transition: 'border-color 0.15s ease',
          userSelect: 'none'
        }}
        onClick={() => onToggleContinuousMode(!continuousMode)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.boxShadow = theme === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.5)'
            : '0 2px 8px rgba(17, 24, 39, 0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.boxShadow = theme === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.4)'
            : '0 2px 8px rgba(17, 24, 39, 0.08)';
        }}
        >
          <div style={{
            width: '36px',
            height: '20px',
            borderRadius: '10px',
            background: continuousMode ? '#1f3a8a' : (theme === 'dark' ? '#4b5563' : '#cfd6df'),
            position: 'relative',
            transition: 'background 0.15s ease'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              left: continuousMode ? '18px' : '2px',
              transition: 'left 0.15s ease',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
            }} />
          </div>
          <div style={{
            fontSize: '0.8125rem',
            fontWeight: '500',
            color: continuousMode
              ? '#1f3a8a'
              : (theme === 'dark' ? '#9ca3af' : '#4b5563')
          }}>
            Continuous Mode
          </div>
        </div>
      )}

      {/* Current Suggestions Display */}
      {continuousMode && currentSuggestions.length > 0 && (
        <div style={{
          maxWidth: 'min(320px, calc(100vw - 2rem))',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          background: theme === 'dark' ? '#1f2937' : '#ffffff',
          border: theme === 'dark' ? '1px solid #374151' : '1px solid #e3e7ed',
          boxShadow: theme === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.4)'
            : '0 2px 8px rgba(17, 24, 39, 0.08)'
        }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: theme === 'dark' ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: '0.5rem'
          }}>
            Suggestions
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            {currentSuggestions.slice(0, 4).map((suggestion, index) => (
              <div key={index} style={{
                fontSize: '0.8125rem',
                color: theme === 'dark' ? '#e5e7eb' : '#111827',
                padding: '0.375rem 0.5rem',
                background: theme === 'dark' ? '#111827' : '#f5f7fa',
                borderRadius: '4px',
                borderLeft: `3px solid ${suggestion.priority === 1 ? '#b42318' : suggestion.priority === 2 ? '#1d4ed8' : '#15803d'}`
              }}>
                {suggestion.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workflow State Display */}
      {continuousMode && workflowState && (
        <div style={{
          maxWidth: 'min(320px, calc(100vw - 2rem))',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          background: theme === 'dark' ? '#1f2937' : '#ffffff',
          border: theme === 'dark' ? '1px solid #374151' : '1px solid #e3e7ed',
          boxShadow: theme === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.4)'
            : '0 2px 8px rgba(17, 24, 39, 0.08)',
          fontSize: '0.8125rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem'
          }}>
            <span style={{ fontWeight: '500', color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>Progress</span>
            <span style={{ fontWeight: '600', color: theme === 'dark' ? '#e5e7eb' : '#111827' }}>
              {workflowState.completionPercent}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '6px',
            background: theme === 'dark' ? '#374151' : '#eef1f5',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${workflowState.completionPercent}%`,
              height: '100%',
              background: '#1f3a8a',
              transition: 'width 0.5s ease'
            }} />
          </div>
          {workflowState.nextMilestone && (
            <div style={{
              marginTop: '0.5rem',
              color: theme === 'dark' ? '#9ca3af' : '#6b7280',
              fontSize: '0.75rem'
            }}>
              Next: {workflowState.nextMilestone}
            </div>
          )}
        </div>
      )}

      {/* Microphone button */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
        {/* Drag handle (move) + hide button so it isn't always on screen */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <div
            title="Drag to move"
            onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX, e.clientY); }}
            onTouchStart={(e) => { if (e.touches[0]) startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.125rem', height: 22, padding: '0 0.5rem 0 0.25rem', borderRadius: 4, cursor: 'grab',
              fontSize: '0.6875rem', fontWeight: 500,
              border: theme === 'dark' ? '1px solid #374151' : '1px solid #cfd6df',
              background: theme === 'dark' ? '#1f2937' : '#ffffff',
              color: theme === 'dark' ? '#9ca3af' : '#4b5563',
              boxShadow: 'none', touchAction: 'none', userSelect: 'none',
            }}
          >
            <MdDragIndicator size={14} aria-hidden="true" />
            Move
          </div>
          <button
            onClick={hideAssistant}
            title="Hide voice assistant"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.125rem', height: 22, padding: '0 0.5rem 0 0.375rem', borderRadius: 4, cursor: 'pointer',
              fontSize: '0.6875rem', fontWeight: 500,
              border: theme === 'dark' ? '1px solid #374151' : '1px solid #cfd6df',
              background: theme === 'dark' ? '#1f2937' : '#ffffff',
              color: theme === 'dark' ? '#9ca3af' : '#4b5563',
              boxShadow: 'none',
            }}
          >
            <MdClose size={12} aria-hidden="true" />
            Hide
          </button>
        </div>
        <button
          onClick={handleClick}
          disabled={!isSupported}
          style={getButtonStyles()}
          title={getButtonLabel()}
          onMouseEnter={(e) => {
            if (isSupported) {
              e.currentTarget.style.transform = 'none';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
          }}
        >
          {getButtonIcon()}
          
          {/* Pulse animation ring for listening/speaking */}
          {(isListening || isSpeaking) && (
            <div style={{
              position: 'absolute',
              inset: '-3px',
              borderRadius: '8px',
              border: '1px solid rgba(17, 24, 39, 0.25)',
              pointerEvents: 'none'
            }} />
          )}
        </button>

        {/* Status label */}
        <div style={{
          fontSize: '0.75rem',
          fontWeight: '500',
          color: theme === 'dark' ? '#9ca3af' : '#4b5563',
          textAlign: 'center',
          minWidth: '120px'
        }}>
          {getButtonLabel()}
        </div>

        {/* Language selector — speak commands in English / Hindi / Marathi */}
        {onChangeVoiceLang && (
          <div style={{ display: 'flex', gap: '0.125rem', background: theme === 'dark' ? '#1f2937' : '#ffffff', border: theme === 'dark' ? '1px solid #374151' : '1px solid #cfd6df', borderRadius: '6px', padding: '0.125rem' }}>
            {[{ k: 'english', t: 'EN' }, { k: 'hindi', t: 'हि' }, { k: 'marathi', t: 'मरा' }].map(({ k, t }) => (
              <button
                key={k}
                onClick={() => onChangeVoiceLang(k)}
                title={k === 'english' ? 'English' : k === 'hindi' ? 'हिन्दी' : 'मराठी'}
                style={{
                  border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
                  padding: '0.2rem 0.55rem', borderRadius: '4px',
                  background: voiceLang === k ? '#1f3a8a' : 'transparent',
                  color: voiceLang === k ? '#fff' : (theme === 'dark' ? '#cbd5e1' : '#4b5563'),
                }}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }

        @keyframes pulseRing {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          100% {
            transform: scale(1.4);
            opacity: 0;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes vaSpin {
          to { transform: rotate(360deg); }
        }

        @keyframes vaBars {
          0%, 100% { transform: scaleY(0.8); }
          50% { transform: scaleY(1.15); }
        }
      `}</style>
    </div>
  );
};

export default VoiceAssistantUI;
